import { useEffect, useMemo, useState } from "react";
import {
  IconAlertTriangle,
  IconArrowsExchange,
  IconCheck,
  IconClock,
  IconRefresh,
  IconUsers,
  IconX,
} from "@tabler/icons-react";
import {
  WorkforceApiError,
  workforceApi,
  WorkforceAssignment,
  WorkforceEscalation,
  WorkforceMembership,
  WorkforceRoutingPreview,
  WorkforceSla,
  WorkforceSummary,
  WorkforceTeam,
} from "./api";
import {
  objectTypeLabel,
  roleLabel,
  statusLabel,
} from "../../localization/zh-CN";
import "./workforce.css";

export type WorkforceView =
  | "my-work"
  | "dispatch"
  | "team-queue"
  | "team-members"
  | "sla-escalation";
const views: Array<{
  key: WorkforceView;
  label: string;
  icon: typeof IconClock;
}> = [
  { key: "my-work", label: "我的工作", icon: IconClock },
  { key: "dispatch", label: "待分发", icon: IconArrowsExchange },
  { key: "team-queue", label: "团队队列", icon: IconUsers },
  { key: "team-members", label: "团队与成员", icon: IconUsers },
  { key: "sla-escalation", label: "SLA 与升级", icon: IconAlertTriangle },
];

export default function WorkforceDevApp({
  view: controlledView,
  embedded = false,
  onViewChange,
}: {
  view?: WorkforceView;
  embedded?: boolean;
  onViewChange?: (view: WorkforceView) => void;
}) {
  const [internalView, setInternalView] = useState<WorkforceView>(readView());
  const view = controlledView ?? internalView;
  const [summary, setSummary] = useState<WorkforceSummary>({});
  const [assignments, setAssignments] = useState<WorkforceAssignment[]>([]);
  const [unassigned, setUnassigned] = useState<WorkforceAssignment[]>([]);
  const [teams, setTeams] = useState<WorkforceTeam[]>([]);
  const [warnings, setWarnings] = useState<WorkforceSla[]>([]);
  const [breaches, setBreaches] = useState<WorkforceSla[]>([]);
  const [escalations, setEscalations] = useState<WorkforceEscalation[]>([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [members, setMembers] = useState<WorkforceMembership[]>([]);
  const [workload, setWorkload] = useState<{
    teamId: string;
    members: WorkforceMembership[];
  }>({ teamId: "", members: [] });
  const [queueAssignments, setQueueAssignments] = useState<
    WorkforceAssignment[]
  >([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [permissionDenied, setPermissionDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    setPermissionDenied(false);
    try {
      const [
        mine,
        pending,
        teamResult,
        warningResult,
        breachResult,
        escalationResult,
      ] = await Promise.all([
        workforceApi.myWork(),
        workforceApi.unassigned(),
        workforceApi.teams(),
        workforceApi.warnings(),
        workforceApi.breaches(),
        workforceApi.escalations(),
      ]);
      setSummary(mine.summary);
      setAssignments(mine.items);
      setUnassigned(pending.items);
      setTeams(teamResult.items);
      setWarnings(warningResult.items);
      setBreaches(breachResult.items);
      setEscalations(escalationResult.items);
      const initialTeam = selectedTeam || teamResult.items[0]?.id || "";
      setSelectedTeam(initialTeam);
      if (initialTeam) await loadTeam(initialTeam);
    } catch (cause) {
      handleError(cause);
    } finally {
      setLoading(false);
    }
  };
  const loadTeam = async (teamId: string) => {
    try {
      const [memberResult, workloadResult, queueResult] = await Promise.all([
        workforceApi.teamMembers(teamId),
        workforceApi.teamWorkload(teamId),
        workforceApi.teamQueue(teamId),
      ]);
      const workloadByUser = new Map(
        workloadResult.members.map((member) => [member.userId, member]),
      );
      setMembers(
        memberResult.items.map((member) => ({
          ...member,
          ...workloadByUser.get(member.userId),
        })),
      );
      setWorkload(workloadResult);
      setQueueAssignments(queueResult.assignments);
    } catch (cause) {
      handleError(cause);
    }
  };
  useEffect(() => {
    void load();
  }, []); // isolated dev module owns its own API lifecycle
  useEffect(() => {
    if (selectedTeam) void loadTeam(selectedTeam);
  }, [selectedTeam]);
  const selectView = (next: WorkforceView) => {
    setInternalView(next);
    onViewChange?.(next);
    if (!embedded)
      window.history.replaceState(null, "", `/_dev/workforce/${next}`);
  };
  const handleError = (cause: unknown) => {
    if (cause instanceof WorkforceApiError && cause.status === 403)
      setPermissionDenied(true);
    setError(describe(cause));
  };
  const act = async (id: string, action: "accept" | "decline") => {
    const row = assignments.find((item) => item.id === id);
    if (!row) return;
    setBusyId(id);
    try {
      await workforceApi[action](id, row.version);
      await load();
    } catch (cause) {
      handleError(cause);
    } finally {
      setBusyId("");
    }
  };
  const filtered = useMemo(
    () =>
      assignments.filter((item) =>
        `${item.objectType} ${item.objectId} ${item.status} ${item.priority}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [assignments, query],
  );
  return (
    <div
      className={
        embedded ? "workforce-dev-shell is-embedded" : "workforce-dev-shell"
      }
    >
      {!embedded ? (
        <aside className="workforce-nav">
          <div className="workforce-brand">
            <span className="workforce-mark">W</span>
            <span>
              <strong>任务协作运营</strong>
              <small>开发环境 · 演示</small>
            </span>
          </div>
          <div className="workforce-scope">
            <span>可信工作空间上下文</span>
            <strong>当前会话工作空间</strong>
            <small>API 决定作用域 · 不跨工作空间</small>
          </div>
          <nav aria-label="任务协作开发导航">
            {views.map(({ key, label, icon: Icon }) => (
              <button
                type="button"
                key={key}
                className={
                  view === key
                    ? "workforce-nav-item is-active"
                    : "workforce-nav-item"
                }
                onClick={() => selectView(key)}
                aria-current={view === key ? "page" : undefined}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </nav>
          <div className="workforce-nav-foot">
            <span className="status-dot" />
            仅开发入口 · 生产禁用
          </div>
        </aside>
      ) : null}
      <main className="workforce-main">
        {!embedded ? (
          <header className="workforce-topbar">
            <div>
              <span className="workforce-eyebrow">
                CRM-WF-22A · 任务协作编排基础
              </span>
              <h1>{views.find((item) => item.key === view)?.label}</h1>
            </div>
            <button
              className="workforce-icon-button"
              type="button"
              onClick={() => void load()}
              aria-label="刷新任务协作数据"
            >
              <IconRefresh size={17} />
            </button>
          </header>
        ) : null}
        <section className="workforce-content">
          {error ? (
            <div className="workforce-alert is-danger" role="alert">
              <IconAlertTriangle size={16} />
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError("")}
                aria-label="关闭错误"
              >
                <IconX size={14} />
              </button>
            </div>
          ) : null}
          {permissionDenied ? (
            <div className="workforce-alert is-warning" role="status">
              当前工作空间没有该任务协作权限；页面保留明确拒绝状态，未伪造成功。
            </div>
          ) : null}
          {loading ? (
            <LoadingState />
          ) : (
            <>
              {view === "my-work" ? (
                <MyWork
                  assignments={filtered}
                  summary={summary}
                  query={query}
                  setQuery={setQuery}
                  busyId={busyId}
                  onAction={act}
                />
              ) : null}
              {view === "dispatch" ? (
                <Dispatch
                  items={unassigned}
                  teams={teams}
                  onAssigned={() => void load()}
                  busyId={busyId}
                  setBusyId={setBusyId}
                  onError={handleError}
                />
              ) : null}
              {view === "team-queue" ? (
                <TeamQueue
                  teams={teams}
                  selectedTeam={selectedTeam}
                  setSelectedTeam={setSelectedTeam}
                  assignments={queueAssignments}
                  workload={workload}
                />
              ) : null}
              {view === "team-members" ? (
                <TeamMembers
                  teams={teams}
                  selectedTeam={selectedTeam}
                  setSelectedTeam={setSelectedTeam}
                  members={members}
                  onUpdated={() => selectedTeam && void loadTeam(selectedTeam)}
                  onError={handleError}
                />
              ) : null}
              {view === "sla-escalation" ? (
                <SlaEscalation
                  warnings={warnings}
                  breaches={breaches}
                  escalations={escalations}
                  onAction={() => void load()}
                  busyId={busyId}
                  setBusyId={setBusyId}
                  onError={handleError}
                />
              ) : null}
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function MyWork({
  assignments,
  summary,
  query,
  setQuery,
  busyId,
  onAction,
}: {
  assignments: WorkforceAssignment[];
  summary: WorkforceSummary;
  query: string;
  setQuery: (value: string) => void;
  busyId: string;
  onAction: (id: string, action: "accept" | "decline") => void;
}) {
  return (
    <>
      <Metrics summary={summary} />
      <div className="workforce-toolbar">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="搜索对象、状态或优先级"
          aria-label="搜索我的工作"
        />
        <span>{assignments.length} 条任务分配</span>
      </div>
      <AssignmentTable
        items={assignments}
        busyId={busyId}
        onAction={onAction}
        empty="暂无属于当前用户的工作"
      />
    </>
  );
}
function Metrics({ summary }: { summary: WorkforceSummary }) {
  const cards = [
    ["pendingAcceptance", "待接受"],
    ["dueToday", "今天到期"],
    ["warning", "即将超时"],
    ["overdue", "已超时"],
    ["review", "等我审核"],
    ["approval", "等我审批"],
    ["waitingCustomer", "等客户"],
    ["waitingInternal", "等内部"],
  ];
  return (
    <div className="workforce-metrics">
      {cards.map(([key, label]) => (
        <div className="workforce-metric" key={key}>
          <span>{label}</span>
          <strong>{summary[key] ?? 0}</strong>
        </div>
      ))}
    </div>
  );
}
function AssignmentTable({
  items,
  busyId,
  onAction,
  onDispatch,
  onPreview,
  previews = {},
  empty,
}: {
  items: WorkforceAssignment[];
  busyId: string;
  onAction?: (id: string, action: "accept" | "decline") => void;
  onDispatch?: (id: string) => void;
  onPreview?: (id: string) => void;
  previews?: Record<string, WorkforceRoutingPreview>;
  empty: string;
}) {
  if (!items.length)
    return (
      <div className="workforce-empty">
        <IconClock size={24} />
        <strong>{empty}</strong>
        <span>数据由隔离的任务协作 API 返回。</span>
      </div>
    );
  return (
    <div className="workforce-table-wrap">
      <table className="workforce-table">
        <thead>
          <tr>
            <th>对象</th>
            <th>工作空间 / 团队</th>
            <th>状态</th>
            <th>优先级</th>
            <th>截止时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <strong>{objectTypeLabel(item.objectType)}</strong>
                <small>{item.objectId}</small>
                {previews[item.id] ? (
                  <small>路由解释：{previews[item.id]!.assignmentReason}</small>
                ) : null}
              </td>
              <td>
                <small>{item.teamId ?? "待分发"}</small>
              </td>
              <td>
                <span
                  className={`workforce-status status-${item.status.toLowerCase().replaceAll("_", "-")}`}
                >
                  {statusLabel(item.status)}
                </span>
              </td>
              <td>{statusLabel(item.priority)}</td>
              <td>
                {formatDate(item.acceptanceDueAt ?? item.processingDueAt)}
              </td>
              <td>
                {onAction &&
                (item.status === "ASSIGNED" ||
                  item.status === "ACCEPTANCE_OVERDUE") ? (
                  <div className="workforce-actions">
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => onAction(item.id, "accept")}
                    >
                      <IconCheck size={14} />
                      接受
                    </button>
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => onAction(item.id, "decline")}
                    >
                      <IconX size={14} />
                      退回
                    </button>
                  </div>
                ) : onDispatch &&
                  onPreview &&
                  item.status === "WAITING_ASSIGNMENT" ? (
                  <div className="workforce-actions">
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => onPreview(item.id)}
                    >
                      路由预览
                    </button>
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() => onDispatch(item.id)}
                    >
                      分发
                    </button>
                  </div>
                ) : (
                  <span className="workforce-muted">只读</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
function Dispatch({
  items,
  teams,
  onAssigned,
  busyId,
  setBusyId,
  onError,
}: {
  items: WorkforceAssignment[];
  teams: WorkforceTeam[];
  onAssigned: () => void;
  busyId: string;
  setBusyId: (value: string) => void;
  onError: (cause: unknown) => void;
}) {
  const [teamId, setTeamId] = useState(teams[0]?.id ?? "");
  const [assigneeId, setAssigneeId] = useState("");
  const [members, setMembers] = useState<WorkforceMembership[]>([]);
  const [previews, setPreviews] = useState<
    Record<string, WorkforceRoutingPreview>
  >({});
  useEffect(() => {
    if (teamId)
      void workforceApi
        .teamMembers(teamId)
        .then((result) => {
          setMembers(result.items);
          setAssigneeId(result.items[0]?.userId ?? "");
        })
        .catch(onError);
  }, [teamId]);
  const assign = async (item: WorkforceAssignment) => {
    if (!teamId || !assigneeId) return;
    setBusyId(item.id);
    try {
      await workforceApi.assign(item.objectType, item.objectId, {
        teamId,
        assigneeUserId: assigneeId,
        reason: "DEMO_DISPATCH_UI",
        priority: item.priority,
        expectedVersion: item.version,
      });
      onAssigned();
    } catch (cause) {
      onError(cause);
    } finally {
      setBusyId("");
    }
  };
  const preview = async (item: WorkforceAssignment) => {
    setBusyId(item.id);
    try {
      const result = await workforceApi.previewRouting({
        workspaceId: item.workspaceId,
        objectType: item.objectType,
        objectId: item.objectId,
        productCategory: "reagent",
        region: "CN",
        requiredSkills: ["chemistry"],
      });
      setPreviews((current) => ({ ...current, [item.id]: result }));
    } catch (cause) {
      onError(cause);
    } finally {
      setBusyId("");
    }
  };
  const find = (id: string) => items.find((item) => item.id === id);
  return (
    <>
      <div className="workforce-panel">
        <div>
          <span className="workforce-eyebrow">路由预览 / 人工分发</span>
          <h2>待分发对象</h2>
        </div>
        <div className="workforce-form-row">
          <label>
            团队
            <select
              value={teamId}
              onChange={(event) => setTeamId(event.target.value)}
            >
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            负责人
            <select
              value={assigneeId}
              onChange={(event) => setAssigneeId(event.target.value)}
            >
              {members
                .filter((member) => member.availabilityStatus === "ACTIVE")
                .map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.user?.displayName ?? member.userId}
                  </option>
                ))}
            </select>
          </label>
        </div>
      </div>
      <AssignmentTable
        items={items}
        busyId={busyId}
        previews={previews}
        onPreview={(id) => {
          const item = find(id);
          if (item) void preview(item);
        }}
        onDispatch={(id) => {
          const item = find(id);
          if (item) void assign(item);
        }}
        empty="当前没有待分发对象"
      />
    </>
  );
}
function TeamQueue({
  teams,
  selectedTeam,
  setSelectedTeam,
  assignments,
  workload,
}: {
  teams: WorkforceTeam[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  assignments: WorkforceAssignment[];
  workload: { members: WorkforceMembership[] };
}) {
  return (
    <>
      <Selector
        teams={teams}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
      />
      <div className="workforce-strip">
        <strong>{assignments.length}</strong>
        <span>活跃队列工作</span>
        <strong>
          {
            workload.members.filter(
              (member) => member.availability === "ACTIVE",
            ).length
          }
        </strong>
        <span>可用成员</span>
        <strong>
          {workload.members.reduce(
            (total, member) => total + (member.activeAssignments ?? 0),
            0,
          )}
        </strong>
        <span>当前任务</span>
      </div>
      <AssignmentTable items={assignments} busyId="" empty="该团队队列为空" />
    </>
  );
}
function TeamMembers({
  teams,
  selectedTeam,
  setSelectedTeam,
  members,
  onUpdated,
  onError,
}: {
  teams: WorkforceTeam[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
  members: WorkforceMembership[];
  onUpdated: () => void;
  onError: (cause: unknown) => void;
}) {
  const update = async (
    member: WorkforceMembership,
    availabilityStatus: string,
  ) => {
    try {
      await workforceApi.updateMembership(member.id, {
        expectedVersion: member.version,
        availabilityStatus,
      });
      onUpdated();
    } catch (cause) {
      onError(cause);
    }
  };
  return (
    <>
      <Selector
        teams={teams}
        selectedTeam={selectedTeam}
        setSelectedTeam={setSelectedTeam}
      />
      <div className="workforce-table-wrap">
        <table className="workforce-table">
          <thead>
            <tr>
              <th>成员</th>
              <th>角色</th>
              <th>技能 / 地区 / 语言</th>
              <th>容量 / 当前负载</th>
              <th>可用性</th>
              <th>代理</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id}>
                <td>
                  <strong>{member.user?.displayName ?? member.userId}</strong>
                  <small>{member.userId}</small>
                </td>
                <td>{roleLabel(member.membershipRole)}</td>
                <td>
                  <small>技能：{member.skills.join(", ") || "—"}</small>
                  <small>地区：{member.regions.join(", ") || "—"}</small>
                  <small>语言：{member.languages.join(", ") || "—"}</small>
                  <small>
                    产品：{member.productCategories.join(", ") || "—"}
                  </small>
                </td>
                <td>
                  {member.capacity ?? member.capacityWeight} /{" "}
                  {member.currentLoad ?? 0}
                </td>
                <td>
                  <select
                    value={member.availabilityStatus}
                    onChange={(event) =>
                      void update(member, event.target.value)
                    }
                  >
                    <option value="ACTIVE">活跃</option>
                    <option value="BUSY">忙碌</option>
                    <option value="ON_LEAVE">休假</option>
                    <option value="UNAVAILABLE">不可用</option>
                    <option value="DELEGATED">已代理</option>
                  </select>
                </td>
                <td>{member.delegationUserId ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
function Selector({
  teams,
  selectedTeam,
  setSelectedTeam,
}: {
  teams: WorkforceTeam[];
  selectedTeam: string;
  setSelectedTeam: (value: string) => void;
}) {
  return (
    <div className="workforce-panel">
      <label>
        选择团队
        <select
          value={selectedTeam}
          onChange={(event) => setSelectedTeam(event.target.value)}
        >
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
function SlaEscalation({
  warnings,
  breaches,
  escalations,
  onAction,
  busyId,
  setBusyId,
  onError,
}: {
  warnings: WorkforceSla[];
  breaches: WorkforceSla[];
  escalations: WorkforceEscalation[];
  onAction: () => void;
  busyId: string;
  setBusyId: (value: string) => void;
  onError: (cause: unknown) => void;
}) {
  const act = async (
    item: WorkforceEscalation,
    action: "acknowledgeEscalation" | "resolveEscalation",
  ) => {
    setBusyId(item.id);
    try {
      await workforceApi[action](item.id, item.version);
      onAction();
    } catch (cause) {
      onError(cause);
    } finally {
      setBusyId("");
    }
  };
  return (
    <>
      <div className="workforce-columns">
        <SlaCard title="预警" items={warnings} />
        <SlaCard title="已超时" items={breaches} />
      </div>
      <div className="workforce-panel">
        <div>
          <span className="workforce-eyebrow">升级历史</span>
          <h2>提醒与升级</h2>
        </div>
        {!escalations.length ? (
          <div className="workforce-empty is-compact">暂无升级事件</div>
        ) : (
          <div className="workforce-escalations">
            {escalations.map((item) => (
              <div className="workforce-escalation" key={item.id}>
                <div>
                  <strong>
                    级别 {item.level} · {statusLabel(item.trigger)}
                  </strong>
                  <small>
                    {item.assignmentId} · {formatDate(item.occurredAt)}
                  </small>
                </div>
                <span
                  className={`workforce-status status-${item.status.toLowerCase()}`}
                >
                  {statusLabel(item.status)}
                </span>
                {item.status !== "RESOLVED" ? (
                  <div className="workforce-actions">
                    <button
                      type="button"
                      disabled={busyId === item.id}
                      onClick={() =>
                        void act(
                          item,
                          item.status === "OPEN"
                            ? "acknowledgeEscalation"
                            : "resolveEscalation",
                        )
                      }
                    >
                      {item.status === "OPEN" ? "确认" : "解决"}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
function SlaCard({ title, items }: { title: string; items: WorkforceSla[] }) {
  return (
    <div className="workforce-panel">
      <div className="workforce-panel-title">
        <h2>{title}</h2>
        <span>{items.length}</span>
      </div>
      {!items.length ? (
        <div className="workforce-empty is-compact">暂无 SLA 记录</div>
      ) : (
        <div className="workforce-sla-list">
          {items.map((item) => (
            <div key={item.id}>
              <IconClock size={14} />
              <span>
                <strong>{statusLabel(item.eventType)}</strong>
                <small>{item.assignmentId}</small>
              </span>
              <time>{formatDate(item.dueAt)}</time>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function LoadingState() {
  return (
    <div className="workforce-empty">
      <div className="workforce-spinner" role="status" aria-label="加载中" />
      <strong>正在读取隔离的任务协作 API…</strong>
    </div>
  );
}
function readView(): WorkforceView {
  const value = window.location.pathname.split("/").pop() as WorkforceView;
  return views.some((item) => item.key === value) ? value : "my-work";
}
function formatDate(value: string | null | undefined): string {
  return value
    ? new Intl.DateTimeFormat("zh-CN", {
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value))
    : "—";
}
function describe(error: unknown): string {
  if (!(error instanceof WorkforceApiError))
    return "任务协作服务暂时不可用，请重试。";
  if (error.status === 401) return "会话已过期，请重新登录。";
  if (error.status === 403) return "当前工作空间没有执行此操作的权限。";
  if (error.status === 409) return "记录已被其他操作更新，请刷新后重试。";
  if (error.status === 422) return "请求参数未通过校验。";
  if (error.code === "REQUEST_TIMEOUT")
    return "任务协作请求超时，未确认写入，请刷新核对后重试。";
  if (error.code === "NETWORK_FAILURE")
    return "网络连接失败，未确认写入，请恢复连接后刷新核对。";
  return "任务协作服务暂时不可用，请稍后重试。";
}
